import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Palette, Pen, Headphones, ArrowRight, Layers, Zap, FileSpreadsheet, Upload } from 'lucide-react';
import BulkImportTasksModal from '../components/BulkImportTasksModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KindCard = ({
  to,
  icon: Icon,
  eyebrow,
  title,
  description,
  bullets,
  examples,
  count,
  cssVars,
  particleColorClass,
  delay,
}) => {
  const navigate = useNavigate();
  const ref = useRef(null);

  // Mouse-tracking spotlight via CSS variables
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${e.clientX - r.left}px`);
    el.style.setProperty('--my', `${e.clientY - r.top}px`);
  };

  return (
    <button
      ref={ref}
      type="button"
      onClick={() => navigate(to)}
      onMouseMove={onMove}
      style={cssVars}
      className="chooser-card group relative overflow-hidden text-left rounded-3xl border border-slate-200/80 bg-white/90 backdrop-blur-sm p-7 hover:shadow-[0_20px_70px_-20px_rgba(15,23,42,0.25)] hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-300 chooser-fade-up"
      data-testid={`submit-kind-${to.split('/').pop()}`}
    >
      {/* Animated scan line on hover */}
      <div className="chooser-scanline" aria-hidden />

      {/* Floating particles (decorative) */}
      <span className={`chooser-particle ${particleColorClass}`} style={{ left: '22%', top: '78%', '--tx': '30px', '--ty': '-90px', animationDelay: '0.4s' }} />
      <span className={`chooser-particle ${particleColorClass}`} style={{ left: '70%', top: '85%', '--tx': '-20px', '--ty': '-110px', animationDelay: '1.6s' }} />
      <span className={`chooser-particle ${particleColorClass}`} style={{ left: '55%', top: '92%', '--tx': '8px', '--ty': '-130px', animationDelay: '2.9s' }} />

      {/* Soft orb top-right */}
      <div className={`pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full blur-3xl opacity-50 chooser-orb-float`} style={{ background: 'var(--chooser-orb-bg)' }} aria-hidden />

      {/* Subtle grid overlay */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04] [background-image:linear-gradient(to_right,#000_1px,transparent_1px),linear-gradient(to_bottom,#000_1px,transparent_1px)] [background-size:24px_24px]" aria-hidden />

      <div className="relative z-[2]">
        {/* Eyebrow tag */}
        <div className="flex items-center justify-between mb-4">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
            <span className="w-1 h-1 rounded-full" style={{ background: 'var(--chooser-grad-a)' }} />
            {eyebrow}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-slate-300 font-semibold">
            {count == null ? '—' : count} active
          </span>
        </div>

        {/* Icon */}
        <div
          className="inline-flex items-center justify-center w-14 h-14 rounded-2xl shadow-lg mb-5 transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-3"
          style={{
            background: 'linear-gradient(135deg, var(--chooser-grad-a), var(--chooser-grad-b))',
            boxShadow: 'var(--chooser-shadow)',
          }}
        >
          <Icon className="h-7 w-7 text-white" strokeWidth={1.75} />
        </div>

        {/* Title */}
        <h3 className="text-2xl font-bold text-slate-900 mb-2 flex items-center gap-2 tracking-tight">
          {title}
          <ArrowRight
            className="h-5 w-5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500"
            style={{ color: 'var(--chooser-grad-a)' }}
          />
        </h3>

        {/* Description */}
        <p className="text-sm text-slate-600 leading-relaxed mb-5" style={{ animationDelay: `${(delay || 0) + 0.1}s` }}>
          {description}
        </p>

        {/* Bullets */}
        <ul className="space-y-1.5 mb-5">
          {bullets.map((b, i) => (
            <li key={b} className="flex items-start gap-2 text-[12.5px] text-slate-600 chooser-fade-up" style={{ animationDelay: `${(delay || 0) + 0.2 + i * 0.05}s` }}>
              <span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: 'var(--chooser-grad-a)' }} />
              <span>{b}</span>
            </li>
          ))}
        </ul>

        {/* Example chips */}
        <div className="flex flex-wrap gap-1.5 mb-6">
          {examples.map((ex, i) => (
            <span
              key={ex}
              className="text-[11px] px-2 py-0.5 rounded-full font-medium border chooser-fade-up"
              style={{
                background: 'var(--chooser-chip-bg)',
                color: 'var(--chooser-chip-fg)',
                borderColor: 'var(--chooser-chip-border)',
                animationDelay: `${(delay || 0) + 0.3 + i * 0.04}s`,
              }}
            >
              {ex}
            </span>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="flex items-center justify-end pt-4 border-t border-slate-100">
          <span
            className="text-xs font-bold flex items-center gap-1 group-hover:gap-2 transition-all"
            style={{ color: 'var(--chooser-grad-a)' }}
          >
            Continue
            <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </button>
  );
};

export default function SubmitTaskChooserPage() {
  const [counts, setCounts] = useState({ design: null, content: null, audio: null });
  const [nextId, setNextId] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const loadCounts = async () => {
    try {
      const [dRes, cRes, aRes, nIdRes] = await Promise.all([
        fetch(`${API_URL}/api/tasks?task_kind=design`, { credentials: 'include' }),
        fetch(`${API_URL}/api/tasks?task_kind=content`, { credentials: 'include' }),
        fetch(`${API_URL}/api/tasks?task_kind=audio`, { credentials: 'include' }),
        fetch(`${API_URL}/api/tasks/next-id`, { credentials: 'include' }),
      ]);
      const [dArr, cArr, aArr, nIdJson] = await Promise.all([
        dRes.ok ? dRes.json() : [],
        cRes.ok ? cRes.json() : [],
        aRes.ok ? aRes.json() : [],
        nIdRes.ok ? nIdRes.json() : {},
      ]);
      setCounts({ design: dArr.length || 0, content: cArr.length || 0, audio: aArr.length || 0 });
      setNextId(nIdJson.next_task_id || null);
    } catch {
      setCounts({ design: 0, content: 0, audio: 0 });
    }
  };

  useEffect(() => {
    loadCounts();
  }, []);

  return (
    <div className="relative max-w-5xl mx-auto" data-testid="submit-task-chooser">
      {/* Ambient background — aurora orbs + drifting grid */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 -left-32 w-[480px] h-[480px] rounded-full blur-3xl opacity-40 chooser-aurora-1" />
        <div className="absolute -top-32 right-0 w-[420px] h-[420px] rounded-full blur-3xl opacity-40 chooser-aurora-2" />
        <div className="absolute inset-0 chooser-grid opacity-60" />
      </div>

      <div className="space-y-10 py-4">
        {/* Header */}
        <div className="text-center space-y-4 chooser-fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-200/80 bg-white/70 backdrop-blur text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            <Sparkles className="h-3 w-3 text-violet-500" />
            New task
            <span className="text-slate-300">·</span>
            <span className="text-emerald-600 inline-flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight leading-[1.05]">
            Initialize a new request.
          </h1>
          <p className="text-[15px] text-slate-500 max-w-xl mx-auto leading-relaxed">
            Three distinct workflows, one unified pipeline. All share the Task Log,
            the same ID sequence, and your routing logic — only the form differs.
          </p>

          {/* Next ID badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-violet-200/70 bg-violet-50/60 backdrop-blur chooser-id-tick">
            <Zap className="h-3.5 w-3.5 text-violet-600" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-violet-700/80">Next ID</span>
            <span className="font-mono font-bold text-violet-700 text-sm">
              {nextId ? <span className="chooser-shimmer-text">{nextId}</span> : '—'}
            </span>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <KindCard
            to="/submit/design"
            icon={Palette}
            eyebrow="Visual production"
            title="Design Task"
            description="Production-ready visual assets bound to brand and channel specifications — every output is dimension-aware and tied to a featured product or SKU."
            bullets={[
              'Required: dimensions, featured product(s)',
              'Auto-populated specs from task type & platform',
              'Routes to design team queue with media-type tagging',
            ]}
            examples={['Banner', 'Meta Ad', 'A+ Content', 'Listing Images', 'Reels Cover']}
            count={counts.design}
            delay={0.05}
            particleColorClass="text-violet-400"
            cssVars={{
              '--chooser-grad-a': '#8b5cf6',
              '--chooser-grad-b': '#d946ef',
              '--chooser-shadow': '0 14px 32px -10px rgba(139,92,246,0.55)',
              '--chooser-spot': 'rgba(139,92,246,0.22)',
              '--chooser-chip-bg': '#f5f3ff',
              '--chooser-chip-fg': '#6d28d9',
              '--chooser-chip-border': '#ede9fe',
              '--chooser-orb-bg': 'radial-gradient(circle, rgba(139,92,246,0.55), transparent 70%)',
            }}
          />

          <KindCard
            to="/submit/content"
            icon={Pen}
            eyebrow="Editorial & copy"
            title="Content Task"
            description="Editorial, scripting, and narrative work where success is measured in voice and accuracy — not pixels. Lightweight intake, flexible references, optional product context."
            bullets={[
              'Optional: dimensions and featured products',
              'References accept both links and free-form notes',
              'Same notification + assignment workflow as design',
            ]}
            examples={['Blog', 'Script', 'Email Copy', 'Caption', 'Voiceover']}
            count={counts.content}
            delay={0.15}
            particleColorClass="text-emerald-400"
            cssVars={{
              '--chooser-grad-a': '#10b981',
              '--chooser-grad-b': '#0d9488',
              '--chooser-shadow': '0 14px 32px -10px rgba(16,185,129,0.55)',
              '--chooser-spot': 'rgba(16,185,129,0.22)',
              '--chooser-chip-bg': '#ecfdf5',
              '--chooser-chip-fg': '#047857',
              '--chooser-chip-border': '#d1fae5',
              '--chooser-orb-bg': 'radial-gradient(circle, rgba(16,185,129,0.55), transparent 70%)',
            }}
          />

          <KindCard
            to="/submit/audio"
            icon={Headphones}
            eyebrow="Sound & voice"
            title="Audio Task"
            description="Voiceovers, jingles, sound design and music edits — brief the sound, the voice and the vibe, and attach sample audio for reference."
            bullets={[
              'Script, language, voice, tone and duration',
              'Upload sample audio (MP3/WAV) as references',
              'Same notification + assignment workflow',
            ]}
            examples={['Voiceover', 'Jingle', 'Sound Design', 'Music Edit']}
            count={counts.audio}
            delay={0.25}
            particleColorClass="text-orange-400"
            cssVars={{
              '--chooser-grad-a': '#f97316',
              '--chooser-grad-b': '#e11d48',
              '--chooser-shadow': '0 14px 32px -10px rgba(249,115,22,0.55)',
              '--chooser-spot': 'rgba(249,115,22,0.22)',
              '--chooser-chip-bg': '#fff7ed',
              '--chooser-chip-fg': '#c2410c',
              '--chooser-chip-border': '#ffedd5',
              '--chooser-orb-bg': 'radial-gradient(circle, rgba(249,115,22,0.55), transparent 70%)',
            }}
          />
        </div>

        {/* Bulk Import — Meta only */}
        <div className="chooser-fade-up" style={{ animationDelay: '0.3s' }}>
          <button
            type="button"
            onClick={() => setBulkOpen(true)}
            className="group relative w-full overflow-hidden rounded-2xl border border-violet-200/70 bg-gradient-to-r from-violet-50/80 via-white to-fuchsia-50/60 backdrop-blur-sm px-5 py-4 text-left transition-all hover:border-violet-400/70 hover:shadow-[0_14px_40px_-14px_rgba(139,92,246,0.45)] hover:-translate-y-0.5"
            data-testid="bulk-import-trigger"
          >
            {/* Subtle scan line */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              <div className="absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-violet-200/50 to-transparent skew-x-12 chooser-scan-anim" />
            </div>

            <div className="relative flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-md shadow-violet-200 shrink-0 transition-transform group-hover:scale-110 group-hover:-rotate-3">
                <FileSpreadsheet className="h-5 w-5 text-white" strokeWidth={1.75} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-violet-600">
                    Power user
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 border border-violet-200">
                    Meta only
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                    Up to 50
                  </span>
                </div>
                <div className="text-[15px] font-bold text-slate-900 leading-tight">
                  Bulk import via Excel
                </div>
                <div className="text-[12px] text-slate-500 mt-0.5">
                  Download a template with live dropdowns (brands, types, SKUs, assignees) and submit up to 50 Meta tasks in a single upload.
                </div>
              </div>
              <span
                className="hidden sm:inline-flex items-center gap-1.5 text-[12px] font-bold text-violet-700 shrink-0 group-hover:gap-2.5 transition-all"
              >
                <Upload className="h-3.5 w-3.5" />
                Start
                <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </div>
          </button>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 text-[11px] text-slate-400 chooser-fade-up" style={{ animationDelay: '0.4s' }}>
          <Layers className="h-3 w-3" />
          <span>Unified log · Sequential IDs · Differentiated routing</span>
        </div>
      </div>

      {/* Bulk Import Modal */}
      <BulkImportTasksModal
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onCommitted={(res) => {
          // Refresh counts + nextId so the chooser stays accurate.
          loadCounts();
          // If at least one task was created, surface a quick path to the Task Log.
          if (res?.created_count > 0) {
            setTimeout(() => {
              // Close modal first; let the user click "Done" — but also offer auto-redirect via toast handled in modal.
            }, 0);
          }
        }}
      />
    </div>
  );
}
